import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
  Skeleton,
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import ClassIcon from '@mui/icons-material/Class';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchStaffMinimal } from '../slices/staffSlice'; // Use minimal API
import {
  fetchClasses,
  updateClass,
  clearUpdateError,
} from '../slices/classesSlice';

const MotionPaper = motion(Paper);

const EditClassPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  // Redux selectors - Use minimal staff data
  const { 
    list: classList, 
    status, 
    updateError, 
    fieldErrors, 
    isUpdating 
  } = useSelector((state) => state.classes);
  const { minimal: staffMinimal, minimalStatus } = useSelector((state) => state.staff);

  // Find the class to edit
  const classToEdit = classList.find((c) => c.id.toString() === id);

  // Local state
  const [classData, setClassData] = useState({
    name: '',
    teacher: '',
  });

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [initError, setInitError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Debug logging - Enhanced
  useEffect(() => {
    console.log('🔍 Component Debug Info:');
    console.log('updateError:', updateError);
    console.log('fieldErrors:', fieldErrors);
    console.log('classData:', classData);
    console.log('hasNameError:', !!(fieldErrors && fieldErrors.name));
    console.log('hasTeacherError:', !!(fieldErrors && fieldErrors.teacher));
  }, [updateError, fieldErrors, classData]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setInitError(null);
        dispatch(clearUpdateError());
        
        await Promise.all([
          dispatch(fetchClasses()).unwrap(),
          dispatch(fetchStaffMinimal({ is_archived: false })).unwrap() // Use minimal API
        ]);
        
        setDataLoaded(true);
      } catch (error) {
        console.error('❌ Failed to load initial data:', error);
        setInitError('فشل في تحميل البيانات المطلوبة');
        setDataLoaded(true);
      }
    };
    
    loadData();
  }, [dispatch]);

  // Set form data when class and staff data are available
  useEffect(() => {
    if (classToEdit && staffMinimal.length > 0 && dataLoaded) {
      // Use full_name from minimal data for more efficient matching
      const matchedTeacher = staffMinimal.find(
        (staff) => staff.full_name.trim() === classToEdit.teacher?.trim()
      );
      
      const formData = {
        name: classToEdit.name || '',
        teacher: matchedTeacher ? matchedTeacher.id : '',
      };
      
      setClassData(formData);
    }
  }, [classToEdit, staffMinimal, dataLoaded]);

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setClassData((prev) => ({ ...prev, [name]: value }));
    
    // Clear field-specific error when user starts typing
    if (fieldErrors && fieldErrors[name]) {
      dispatch(clearUpdateError());
    }
    
    if (initError) {
      setInitError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!classData.name.trim()) {
      setInitError('اسم الصف مطلوب');
      return;
    }
    
    if (!classData.teacher) {
      setInitError('المعلم المسؤول مطلوب');
      return;
    }
    
    setInitError(null);
    dispatch(clearUpdateError());
    
    try {
      const result = await dispatch(updateClass({ id, data: classData })).unwrap();
      console.log('✅ Update successful:', result);
      navigate('/classes');
    } catch (error) {
      console.error('❌ Update failed:', error);
      // Error will be handled by Redux and component will re-render
    }
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
    dispatch(clearUpdateError());
  };

  const handleCancel = () => {
    navigate('/classes');
  };

  // Show snackbar for non-field errors
  useEffect(() => {
    if (updateError?.message && !fieldErrors?.name && !fieldErrors?.teacher) {
      setShowSnackbar(true);
    }
  }, [updateError, fieldErrors]);

  // Available teachers using minimal data
  const availableTeachers = staffMinimal.filter(teacher => 
    teacher.is_teacher && !teacher.is_archived
  );

  // Get selected teacher details for display
  const selectedTeacher = availableTeachers.find(teacher => teacher.id === classData.teacher);

  // Loading state
  if (!dataLoaded || status === 'loading') {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          bgcolor: '#F8F9FF',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={44} sx={{ color: '#4338CA' }} />
        <Typography sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
          جاري تحميل بيانات الصف...
        </Typography>
      </Box>
    );
  }

  // Error state if class not found
  if (dataLoaded && classList.length > 0 && !classToEdit) {
    return (
      <Box
        sx={{
          direction: 'rtl',
          fontFamily: 'Cairo, Tajawal, sans-serif',
          bgcolor: '#F8F9FF',
          minHeight: '100vh',
          p: { xs: 2, md: 3 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 500,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            textAlign: 'center',
          }}
        >
          <Alert severity="error" sx={{ borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              الصف المطلوب غير موجود
            </Typography>
          </Alert>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#4338CA',
              color: '#4338CA',
              '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
            }}
          >
            العودة إلى قائمة الصفوف
          </Button>
        </Paper>
      </Box>
    );
  }

  // Check for errors
  const hasNameError = !!(fieldErrors && fieldErrors.name);
  const hasTeacherError = !!(fieldErrors && fieldErrors.teacher);

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EditIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                تعديل الصف
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {classToEdit ? `تعديل بيانات الصف: ${classToEdit.name}` : 'تحديث اسم الصف والمعلم المسؤول'}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={handleCancel}
            disabled={isUpdating}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            العودة للصفوف
          </Button>
        </MotionPaper>

        {/* Form Card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          {classToEdit && (
            <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
              <ClassIcon sx={{ color: '#4338CA', fontSize: 20 }} />
              <Chip
                label={`تعديل: ${classToEdit.name}`}
                variant="outlined"
                color="primary"
                size="medium"
                sx={{
                  fontWeight: 700,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  bgcolor: '#EEF2FF',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              />
            </Stack>
          )}

          {/* Initial errors */}
          {initError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              {initError}
            </Alert>
          )}

          {/* General error alert */}
          {updateError?.message && !hasNameError && !hasTeacherError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              {updateError.message}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Class Name Field */}
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1E1B4B',
                  mb: 1,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                اسم الصف
              </Typography>
              <TextField
                label="اسم الصف"
                name="name"
                variant="outlined"
                fullWidth
                required
                value={classData.name}
                onChange={handleChange}
                error={hasNameError}
                helperText={
                  hasNameError
                    && (fieldErrors.name || '').trim()
                }
                disabled={isUpdating}
                placeholder="مثال: الصف الأول أ"
                sx={{
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
                  },
                  '& label.Mui-focused': { color: '#4338CA' },
                  '& .MuiFormHelperText-root.Mui-error': {
                    color: '#DC2626',
                    fontWeight: 'bold',
                  },
                }}
              />
            </Box>

            {/* Enhanced Teacher Selection Field */}
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1E1B4B',
                  mb: 1,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                اسم المعلم المسؤول
              </Typography>
              <FormControl
                fullWidth
                required
                error={hasTeacherError}
                disabled={isUpdating}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
                  },
                  '& label.Mui-focused': { color: '#4338CA' },
                }}
              >
              <InputLabel>اسم المعلم المسؤول</InputLabel>
              <Select
                label="اسم المعلم المسؤول"
                name="teacher"
                value={classData.teacher}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>اختر المعلم المسؤول</em>
                </MenuItem>
                
                {minimalStatus === 'loading' ? (
                  <MenuItem disabled>
                    <Stack useFlexGap direction="row" spacing={2} alignItems="center" width="100%">
                      <Skeleton variant="circular" width={32} height={32} />
                      <Skeleton variant="text" width="60%" />
                    </Stack>
                  </MenuItem>
                ) : (
                  availableTeachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'primary.main',
                            fontSize: '12px'
                          }}
                        >
                          <AccountCircle />
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {teacher.full_name}
                          </Typography>
                          {teacher.employee_id && (
                            <Typography variant="caption" color="text.secondary">
                              رقم الموظف: {teacher.employee_id}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))
                )}
                
                {availableTeachers.length === 0 && minimalStatus !== 'loading' && (
                  <MenuItem disabled>
                    <Typography color="text.secondary" textAlign="center" width="100%">
                      لا توجد معلمين متاحين
                    </Typography>
                  </MenuItem>
                )}
              </Select>
              
              {hasTeacherError && (
                <Typography variant="caption" sx={{ mt: 0.5, mr: 2, fontWeight: 'bold', color: '#DC2626', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {(fieldErrors.teacher || '').trim()}
                </Typography>
              )}
              </FormControl>
            </Box>

            {/* Selected Teacher Preview */}
            {selectedTeacher && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#EEF2FF',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#4338CA', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  المعلم المختار:
                </Typography>
                <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: '#4338CA' }}>
                    <AccountCircle />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                      {selectedTeacher.full_name}
                    </Typography>
                    {selectedTeacher.employee_id && (
                      <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        رقم الموظف: {selectedTeacher.employee_id}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}

            <Divider sx={{ borderColor: '#E5E7EB' }} />
            
            {/* Action Buttons */}
            <Stack useFlexGap direction="row" spacing={2} justifyContent="flex-end" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancel}
                disabled={isUpdating}
                sx={{
                  fontWeight: 700,
                  py: 1.3,
                  minWidth: 120,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#E5E7EB',
                  color: '#6B7280',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF', color: '#4338CA' },
                }}
              >
                إلغاء
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={
                  isUpdating ||
                  availableTeachers.length === 0 ||
                  minimalStatus === 'loading'
                }
                sx={{
                  fontWeight: 700,
                  py: 1.3,
                  minWidth: 140,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  bgcolor: '#4338CA',
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
                }}
              >
                {isUpdating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    جاري الحفظ...
                  </>
                ) : (
                  'حفظ التعديلات'
                )}
              </Button>
            </Stack>
          </Stack>
        </form>
        </MotionPaper>
      </Box>

      {/* Snackbar for general errors */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 2, fontFamily: 'Cairo, Tajawal, sans-serif' }}
        >
          {updateError?.message || 'حدث خطأ في تعديل الصف'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditClassPage;